// S4 (v0.15.0) — Public accept-invite page.
//
// Route: /accept-invite?token=…  (public; rendered outside RBACGuard)
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckIcon, XIcon } from "lucide-react";

import { useAcceptInvite, PASSWORD_RULES } from "@/api/s4";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuthStore } from "@/store/auth";
import { decodeJwt, useTenantStore } from "@/store/tenant";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const accept = useAcceptInvite();
  const nav = useNavigate();

  const ruleState = useMemo(
    () =>
      PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(password) })),
    [password],
  );
  const allPass = ruleState.every((r) => r.passed);
  const passwordsMatch = password.length > 0 && password === confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (!passwordsMatch) {
      setServerError("Passwords do not match");
      return;
    }
    try {
      const result = await accept.mutateAsync({
        token,
        password,
        full_name: fullName.trim() || undefined,
      });
      // Land logged in. S-SEC-3 — the accept-invite endpoint set the
      // httpOnly auth cookies; we don't store tokens in JS. The body
      // token is used transiently only to seed the tenant store, and
      // RBACGuard will rehydrate the user via /me on the next route.
      useAuthStore.getState().setAuthChecked(false);
      const claims = decodeJwt(result.access_token);
      if (claims) {
        useTenantStore.getState().setIsSuperAdmin(claims.is_super_admin);
        useTenantStore.getState().setTenant(claims.tenant_id, "", "");
      }
      nav("/live", { replace: true });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setServerError(
        typeof detail === "string" ? detail : "Failed to create account",
      );
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <h1 className="text-lg font-semibold">Invalid invitation link.</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Please contact your administrator for a new invitation.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-6">
          <div className="text-center">
            <div className="mx-auto mb-2 inline-block rounded bg-primary px-3 py-1 text-sm font-semibold text-white">
              SView360
            </div>
            <h1 className="mt-2 text-xl font-semibold">Welcome to SView360</h1>
            <p className="text-sm text-muted-foreground">
              Create your password to complete account setup.
            </p>
          </div>

          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="full_name">Your name (optional)</Label>
              <Input
                id="full_name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Anu Reddy"
              />
            </div>
            <div>
              <Label htmlFor="pw">New password</Label>
              <PasswordInput
                id="pw"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <PasswordInput
                id="confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
              {confirm.length > 0 && !passwordsMatch && (
                <p className="mt-1 text-xs text-red-600">
                  Passwords do not match
                </p>
              )}
            </div>

            <div className="rounded border border-border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Password requirements
              </p>
              <ul className="space-y-1 text-xs">
                {ruleState.map((r) => (
                  <li
                    key={r.key}
                    className={`flex items-center gap-2 ${
                      r.passed ? "text-emerald-600" : "text-muted-foreground"
                    }`}
                  >
                    {r.passed ? (
                      <CheckIcon className="h-3 w-3" />
                    ) : (
                      <XIcon className="h-3 w-3 text-red-500" />
                    )}
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>

            {serverError && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {serverError}
              </p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#ff6d00] text-white hover:bg-[#ff6d00]/90"
              disabled={!allPass || !passwordsMatch || accept.isPending}
            >
              {accept.isPending ? "Creating account…" : "Create Account & Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
